import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {finalize} from 'rxjs';
import {AuthService} from '../../../services/auth.service';
import {HttpErrorResponse} from '@angular/common/http';
import {
  IAccountActivationRequest,
  IAuthEngineHttpBaseResponse,
  ICheckAccountActivationOtpResponseData
} from '../../../interfaces';
import {EUserType} from '../../../enums/user-type.enum';
import {
  ExtractHttpErrorResponseCode, ExtractHttpErrorResponseCodeAndMessage,
  ExtractHttpErrorResponseMessage,
  IHttpErrorResponseCodeAndMessage
} from "@zikata-frontend/util";

type TActivationStatus = 'otp' | 'activated' | 'pending' | 'success' | 'failure' | 'expired' | 'tokenSent';

@Component({
  selector: 'zikata-frontend-non-citizen-account-activation',
  templateUrl: './non-citizen-account-activation.component.html',
})
export class NonCitizenAccountActivationComponent implements OnInit {
  @Input() authorizationServiceBaseUrl!: string;
  @Input() authorizationClientId!: string;

  @Output() OnGoToResendOtp: EventEmitter<boolean> = new EventEmitter();
  @Output() OnOtpSubmit: EventEmitter<string> = new EventEmitter();
  @Output() OnContinue: EventEmitter<boolean> = new EventEmitter();
  @Output() OnGoToLogin: EventEmitter<boolean> = new EventEmitter();

  oneTimePasswordToken: string;
  oneTimePasswordSent: string;
  accountActivationRequestStatus?: TActivationStatus;
  activationMethod: 'code' | 'link' = 'code';
  isSubmitting = false;
  isVerifyingToken = true;
  defaultFailureTitle = 'Account Activation failed';
  defaultFailureMessage = 'Error encountered while activating account';
  defaultFailureResendDescription = 'Do you want another link?';
  defaultSuccessTitle = 'Account Activated Successfully';
  defaultSuccessMessage = 'Your account has been successfully activated. Click below to login';
  defaultActivatedErrorTitle = 'Account already Activated';
  defaultActivatedErrorMessage =
    'The activation link you are trying to access is from an account that is already activated. Click the button below to login';


  failureTitle = this.defaultFailureTitle;
  failureMessage = this.defaultFailureMessage;
  failureResendDescription = this.defaultFailureResendDescription;
  successTitle = this.defaultSuccessTitle;
  successMessage = this.defaultSuccessMessage;
  activatedErrorMessage = '';

  constructor(private authService: AuthService, private activatedRoute: ActivatedRoute) {
    this.oneTimePasswordToken = this.activatedRoute.snapshot.queryParamMap.get('token') ?? '';
    this.oneTimePasswordSent = this.activatedRoute.snapshot.queryParamMap.get('tokenSent') ?? '';
  }

  ngOnInit() {
    if (this.oneTimePasswordSent && this.oneTimePasswordSent === 'true') {
      const displayedMessage = 'A new activation token has been sent to your email.\nPlease check your email to activate your account';
      this.updateSuccessMessage('Email sent', displayedMessage);
      this.accountActivationRequestStatus = 'success';
      this.isVerifyingToken = false;
    } else if (this.oneTimePasswordToken) {
      this.checkActivationOtp();
    } else {
      this.isVerifyingToken = false;
      this.accountActivationRequestStatus = 'otp';
      this.activationMethod = 'code';
    }
  }

  checkActivationOtp(): void {
    this.authService.checkActivationOtp(
      this.authorizationServiceBaseUrl,
      this.oneTimePasswordToken,
      EUserType.CLIENT
    )
      .pipe(finalize(() => (this.isVerifyingToken = false)))
      .subscribe({
        next: (response: IAuthEngineHttpBaseResponse<ICheckAccountActivationOtpResponseData>) => {
          if (response.data) {
            this.accountActivationRequestStatus = 'pending';
            return;
          }
          this.accountActivationRequestStatus = 'failure';
        },
        error: (error: HttpErrorResponse) => {
          const errorCode: string | null = ExtractHttpErrorResponseCode(error);
          if (errorCode && errorCode === 'ACCOUNT_ALREADY_ACTIVATED') {
            const message: string = ExtractHttpErrorResponseMessage(error, 'Account already activated');
            this.updateActivationErrorMessage(message);
            this.accountActivationRequestStatus = 'activated';
            return;
          }

          if (errorCode === 'otp_8007' || errorCode === 'otp_8005' || errorCode === 'OTP_EXPIRED') {
            this.updateFailureMessage(
              'Link Expired',
              ExtractHttpErrorResponseMessage(error, 'Sorry, the link to activate your account has expired')
            );
            this.accountActivationRequestStatus = 'expired';
            return;
          }

          if (errorCode === 'OTP_NOT_FOUND') {
            this.updateFailureMessage(
              'Account already activated',
              'Sorry, this email is already activated please log in to proceed'
            );
            this.accountActivationRequestStatus = 'failure';
            return;
          }

          const errorMessage = 'Could not Activate Account';
          const title: string = ExtractHttpErrorResponseMessage(error, 'Activation Failed');

          this.updateFailureMessage(title, errorMessage);

          this.accountActivationRequestStatus = 'failure';
        },
      });
  }

  onActivationFormSubmit(password: string) {
    const activateAccountRequest: IAccountActivationRequest = {
      password,
      clientId: this.authorizationClientId,
      oneTimePasswordToken: this.oneTimePasswordToken,
      userType: EUserType.CLIENT,
    };

    this.updateActivationErrorMessage('')

    this.isSubmitting = true;
    this.authService.activateAccount(
      this.authorizationServiceBaseUrl,
      activateAccountRequest
    )
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response: { message: string }) => {
          this.updateFailureMessage(response.message, this.defaultSuccessMessage);
          this.accountActivationRequestStatus = 'success';
        },
        error: (error: HttpErrorResponse) => {
          const codeAndMessage: IHttpErrorResponseCodeAndMessage = ExtractHttpErrorResponseCodeAndMessage(error, 'Could activate account');
          const title = 'Activation activation failed.';

          this.updateActivationErrorMessage(codeAndMessage.message);

          if (codeAndMessage.code === 'OTP_EXPIRED') {
            this.updateFailureMessage(title, codeAndMessage.message);
            this.accountActivationRequestStatus = 'failure';
          }
        },
      });
  }

  updateFailureMessage(
    title: string = this.defaultFailureTitle,
    message: string = this.defaultFailureMessage,
    resendDescription: string = this.defaultFailureResendDescription
  ) {
    this.failureTitle = title;
    this.failureMessage = message;
    this.failureResendDescription = resendDescription;
  }

  updateSuccessMessage(title: string = this.defaultSuccessTitle, description: string = this.defaultSuccessMessage) {
    this.successTitle = title;
    this.successMessage = description;
  }

  updateActivationErrorMessage(message: string = this.defaultActivatedErrorMessage) {
    this.activatedErrorMessage = message;
  }

  resendWithExpiredOtp() {
    this.isVerifyingToken = true;
    this.authService
      .resendActivationOtpUsingExpiredToken(
        this.authorizationServiceBaseUrl,
        this.authorizationClientId,
        this.oneTimePasswordToken
      )
      .pipe(finalize(() => (this.isVerifyingToken = false)))
      .subscribe({
        next: () => {
          const displayedMessage = 'A new activation token has been sent to your email';
          this.updateSuccessMessage('Email sent', displayedMessage);
          this.accountActivationRequestStatus = 'tokenSent';
        },
        error: (error: HttpErrorResponse) => {
          this.updateFailureMessage(
            'Token not sent',
            error.error?.message ? error.error.message : 'We could not send a new token',
            'Do you want to try again?'
          );
          this.accountActivationRequestStatus = 'expired';
        },
      });
  }

  onContinue() {
    this.OnContinue.emit(true);
  }

  onGoToLogin() {
    this.OnGoToLogin.emit(true);
  }

  onOtpSubmit($event: string) {
    this.OnOtpSubmit.emit($event);
  }
}
